/**
 * Smart Medication Dashboard Component
 *
 * Enhanced medication administration dashboard with smart visibility rules,
 * two-timestamp tracking, and real-time status calculation.
 *
 * Features:
 * - 24h smart visibility window (grace period + upcoming medications)
 * - Cross-midnight boundary support
 * - Real-time status calculation
 * - Industry-standard status categories
 * - Justification workflow integration
 */

import React, { useState, useEffect } from 'react';
import {
    Card,
    Row,
    Col,
    Table,
    Button,
    Input,
    Select,
    Space,
    Tag,
    Badge,
    Modal,
    message,
    Tooltip,
    Statistic,
    Alert,
    Timeline,
    Typography,
    Avatar,
    Empty
} from 'antd';
import {
    PlusOutlined,
    SearchOutlined,
    EyeOutlined,
    PlayCircleOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    WarningOutlined,
    ReloadOutlined,
    MedicineBoxOutlined,
    UserOutlined,
    ExclamationCircleOutlined,
    CloseCircleOutlined,
    InfoCircleOutlined
} from '@ant-design/icons';
import moment from 'moment';

import medpassService from '../../services/medpassService';
import AdministrationSessionManager from './AdministrationSession/AdministrationSessionManager';
import AdministrationHistoryButton from './AdministrationSession/AdministrationHistoryButton';

const { Option } = Select;
const { Search } = Input;
const { Title, Text } = Typography;

const SmartMedicationDashboard = ({ selectedPatientId = null, embeddedMode = false }) => {
    const [patients, setPatients] = useState([]);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [showAdminModal, setShowAdminModal] = useState(false);
    const [selectedMedication, setSelectedMedication] = useState(null);
    const [currentTime, setCurrentTime] = useState(moment());

    // Update current time every minute for real-time status
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(moment());
        }, 60000);
        return () => clearInterval(timer);
    }, []);

    // Handle embedded mode with preselected patient
    useEffect(() => {
        if (embeddedMode && selectedPatientId) {
            // Create patient object for embedded mode
            const embeddedPatient = {
                id: selectedPatientId,
                // These will be populated when dashboard data loads
                full_name: 'Loading...',
                mrn: 'Loading...'
            };
            setSelectedPatient(embeddedPatient);
        }
    }, [embeddedMode, selectedPatientId]);

    // Load dashboard data when patient changes
    useEffect(() => {
        if (selectedPatient) {
            loadMedicationDashboard();
            // Auto-refresh every 5 minutes
            const interval = setInterval(loadMedicationDashboard, 5 * 60 * 1000);
            return () => clearInterval(interval);
        }
    }, [selectedPatient]);

    const searchPatients = async (query) => {
        if (!query || query.length < 2) {
            setPatients([]);
            return;
        }

        try {
            const response = await medpassService.searchPatients(query);
            if (response.success) {
                setPatients(response.data || []);
            }
        } catch (error) {
            console.error('Error searching patients:', error);
        }
    };

    const loadMedicationDashboard = async () => {
        if (!selectedPatient) return;

        setLoading(true);
        try {
            const response = await medpassService.getMedicationDashboard(selectedPatient.id);
            if (response.success) {
                setDashboardData(response.data);
                
                // Update patient info if in embedded mode and we have patient data
                if (embeddedMode && response.data?.patient) {
                    setSelectedPatient(prev => ({
                        ...prev,
                        full_name: response.data.patient.full_name || response.data.patient.name,
                        mrn: response.data.patient.mrn
                    }));
                }
            } else {
                message.error(response.message || 'Failed to load medication dashboard');
            }
        } catch (error) {
            console.error('Error loading dashboard:', error);
            message.error('Failed to load medication dashboard');
        } finally {
            setLoading(false);
        }
    };

    const handlePatientSelect = (patientId) => {
        const patient = patients.find(p => p.id === patientId);
        if (patient) {
            setSelectedPatient(patient);
            message.success(`Selected patient: ${patient.full_name}`);
        }
    };

    const handleAdministration = (medication) => {
        console.log('🚀 handleAdministration called with medication:', medication);
        console.log('🚀 Setting showAdminModal to true');
        setSelectedMedication(medication);
        setShowAdminModal(true);
    };

    // Smart status calculation and display
    const getMedicationStatusConfig = (status) => {
        const configs = {
            'pending': {
                color: 'default',
                text: 'Pending',
                icon: <ClockCircleOutlined />,
                bgColor: '#f5f5f5'
            },
            'due_now': {
                color: 'processing',
                text: 'Due Now',
                icon: <ExclamationCircleOutlined />,
                bgColor: '#e6f7ff'
            },
            'overdue': {
                color: 'warning',
                text: 'Overdue',
                icon: <WarningOutlined />,
                bgColor: '#fff7e6'
            },
            'missed': {
                color: 'error',
                text: 'Missed',
                icon: <CloseCircleOutlined />,
                bgColor: '#fff2f0'
            },
            'administered_on_time': {
                color: 'success',
                text: 'Administered',
                icon: <CheckCircleOutlined />,
                bgColor: '#f6ffed'
            },
            'administered_early': {
                color: 'success',
                text: 'Early',
                icon: <CheckCircleOutlined />,
                bgColor: '#f6ffed'
            },
            'administered_late': {
                color: 'warning',
                text: 'Late',
                icon: <CheckCircleOutlined />,
                bgColor: '#fff7e6'
            },
            'cancelled': {
                color: 'error',
                text: 'Cancelled',
                icon: <CloseCircleOutlined />,
                bgColor: '#fff2f0'
            }
        };
        return configs[status] || {
            color: 'default',
            text: status || 'Unknown',
            icon: <InfoCircleOutlined />,
            bgColor: '#f5f5f5'
        };
    };

    const renderPatientSelector = () => (
        <Card
            title={
                <Space>
                    <SearchOutlined />
                    Patient Selection
                </Space>
            }
            size="small"
            style={{ marginBottom: 16 }}
        >
            <Space direction="vertical" style={{ width: '100%' }}>
                <Search
                    placeholder="Search patient by name, MRN, or ID..."
                    onSearch={searchPatients}
                    onChange={(e) => setSearchValue(e.target.value)}
                    size="large"
                    allowClear
                />

                {patients.length > 0 && (
                    <Select
                        style={{ width: '100%' }}
                        placeholder="Select a patient from search results"
                        onSelect={handlePatientSelect}
                        size="large"
                    >
                        {patients.map(patient => (
                            <Option key={patient.id} value={patient.id}>
                                <Space>
                                    <Avatar size="small" icon={<UserOutlined />} />
                                    <div>
                                        <div style={{ fontWeight: 'bold' }}>
                                            {patient.full_name || patient.name}
                                        </div>
                                        <Text type="secondary" style={{ fontSize: '12px' }}>
                                            MRN: {patient.mrn} | DOB: {moment(patient.date_of_birth).format('MMM DD, YYYY')}
                                        </Text>
                                    </div>
                                </Space>
                            </Option>
                        ))}
                    </Select>
                )}

                {selectedPatient && (
                    <Card
                        size="small"
                        style={{
                            backgroundColor: '#f6ffed',
                            border: '2px solid #52c41a'
                        }}
                    >
                        <Space>
                            <Avatar icon={<UserOutlined />} style={{ backgroundColor: '#52c41a' }} />
                            <div>
                                <div style={{ fontWeight: 'bold' }}>
                                    {selectedPatient.full_name || selectedPatient.name}
                                </div>
                                <Text type="secondary">
                                    MRN: {selectedPatient.mrn} | Selected
                                </Text>
                            </div>
                        </Space>
                    </Card>
                )}
            </Space>
        </Card>
    );

    const renderDashboardSummary = () => {
        if (!dashboardData?.summary) return null;

        const { summary } = dashboardData;

        return (
            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="Due Now"
                            value={summary.due_now || 0}
                            valueStyle={{ color: '#fa8c16' }}
                            prefix={<ExclamationCircleOutlined />}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="Overdue"
                            value={summary.overdue || 0}
                            valueStyle={{ color: '#f5222d' }}
                            prefix={<WarningOutlined />}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="Can Administer"
                            value={summary.can_administer || 0}
                            valueStyle={{ color: '#52c41a' }}
                            prefix={<MedicineBoxOutlined />}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="Completed Today"
                            value={summary.completed_today || 0}
                            valueStyle={{ color: '#1890ff' }}
                            prefix={<CheckCircleOutlined />}
                        />
                    </Card>
                </Col>
            </Row>
        );
    };

    const renderMedicationTimeline = () => {
        if (!dashboardData?.medications || dashboardData.medications.length === 0) {
            return (
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No medications found for the selected patient"
                />
            );
        }

        return (
            <Timeline>
                {dashboardData.medications.map((med, index) => {
                    const statusConfig = getMedicationStatusConfig(med.dynamic_status);
                    const scheduledTime = moment(med.scheduled_time);
                    const isToday = scheduledTime.isSame(moment(), 'day');
                    const isWithinGracePeriod = med.can_administer && !med.is_completed;

                    return (
                        <Timeline.Item
                            key={`${med.medication_id}-${index}`}
                            color={statusConfig.color}
                            dot={statusConfig.icon}
                        >
                            <Card
                                size="small"
                                style={{
                                    border: isWithinGracePeriod ? '2px solid #1890ff' : '1px solid #d9d9d9',
                                    backgroundColor: statusConfig.bgColor,
                                    marginBottom: 8
                                }}
                            >
                                <Row justify="space-between" align="middle">
                                    <Col span={12}>
                                        <div>
                                            <Title level={5} style={{ margin: 0, color: '#262626' }}>
                                                {med.medication_name}
                                            </Title>
                                            <Space size="small" style={{ marginTop: 4 }}>
                                                <Text type="secondary">💊 {med.dose_amount}</Text>
                                                <Text type="secondary">📍 {med.route}</Text>
                                                <Text type="secondary">
                                                    🕐 {scheduledTime.format('DD/MM/YYYY hh:mm A')}
                                                </Text>
                                                {!isToday && (
                                                    <Tag color="purple" size="small">
                                                        {scheduledTime.fromNow()}
                                                    </Tag>
                                                )}
                                            </Space>
                                        </div>
                                    </Col>
                                    <Col span={6} style={{ textAlign: 'center' }}>
                                        <Tag
                                            color={statusConfig.color}
                                            icon={statusConfig.icon}
                                            style={{ fontSize: '13px', padding: '4px 8px' }}
                                        >
                                            {statusConfig.text}
                                        </Tag>
                                    </Col>
                                    <Col span={6} style={{ textAlign: 'right' }}>
                                        <Space>
                                            {isWithinGracePeriod && (
                                                <Button
                                                    type="primary"
                                                    icon={<PlayCircleOutlined />}
                                                    onClick={() => handleAdministration(med)}
                                                    style={{
                                                        background: med.dynamic_status === 'overdue' ? '#f5222d' : '#52c41a',
                                                        borderColor: med.dynamic_status === 'overdue' ? '#f5222d' : '#52c41a'
                                                    }}
                                                >
                                                    {med.dynamic_status === 'overdue' ? 'Administer (Late)' : 'Start Administration'}
                                                </Button>
                                            )}
                                            {med.is_completed && (
                                                <Button
                                                    icon={<EyeOutlined />}
                                                    onClick={() => {
                                                        // Show administration details
                                                        Modal.info({
                                                            title: 'Administration Details',
                                                            content: (
                                                                <div>
                                                                    <p><strong>Medication:</strong> {med.medication_name}</p>
                                                                    <p><strong>Status:</strong> {statusConfig.text}</p>
                                                                    <p><strong>Scheduled:</strong> {scheduledTime.format('DD/MM/YYYY hh:mm A')}</p>
                                                                    {med.administration_record && (
                                                                        <>
                                                                            <p><strong>Administered:</strong> {moment(med.administration_record.administered_at).format('DD/MM/YYYY hh:mm A')}</p>
                                                                            <p><strong>Notes:</strong> {med.administration_record.administration_notes || 'None'}</p>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            )
                                                        });
                                                    }}
                                                >
                                                    View Details
                                                </Button>
                                            )}
                                        </Space>
                                    </Col>
                                </Row>
                            </Card>
                        </Timeline.Item>
                    );
                })}
            </Timeline>
        );
    };

    return (
        <div className="smart-medication-dashboard">
            {/* Header */}
            <Card style={{ marginBottom: 16 }}>
                <Row justify="space-between" align="middle">
                    <Col>
                        <Title level={3} style={{ margin: 0 }}>
                            <MedicineBoxOutlined /> Smart Medication Dashboard
                        </Title>
                        <Text type="secondary">
                            Enhanced medication administration with 24h smart visibility
                        </Text>
                    </Col>
                    <Col>
                        <Space>
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={loadMedicationDashboard}
                                loading={loading}
                                disabled={!selectedPatient}
                            >
                                Refresh
                            </Button>
                            {selectedPatient && (
                                <AdministrationHistoryButton
                                    patientId={selectedPatient.id}
                                    patientName={selectedPatient.full_name || selectedPatient.name}
                                    size="middle"
                                    type="default"
                                />
                            )}
                        </Space>
                    </Col>
                </Row>
            </Card>

            {/* Patient Selector - Only show in standalone mode */}
            {!embeddedMode && renderPatientSelector()}

            {/* Dashboard Content */}
            {selectedPatient ? (
                <>
                    {/* Summary Statistics */}
                    {renderDashboardSummary()}

                    {/* Smart Visibility Alert */}
                    <Alert
                        message="Smart 24-Hour Visibility Window Active"
                        description="Showing unadministered medications up to 24h grace period before current time + upcoming medications for next 24h"
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />

                    {/* Medication Timeline */}
                    <Card
                        title={
                            <Space>
                                <MedicineBoxOutlined />
                                Medications Timeline
                                {dashboardData?.medications && (
                                    <Badge
                                        count={dashboardData.medications.length}
                                        style={{ backgroundColor: '#52c41a' }}
                                    />
                                )}
                            </Space>
                        }
                        loading={loading}
                    >
                        {renderMedicationTimeline()}
                    </Card>
                </>
            ) : (
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="Select a patient to view their smart medication dashboard"
                />
            )}

            {/* Enhanced Administration Session Manager */}
            <style>
                {`
                .administration-session-modal .ant-modal-wrap {
                    z-index: 5000 !important;
                }
                .administration-session-modal .ant-modal-mask {
                    z-index: 4999 !important;
                }
                .administration-session-modal .ant-modal {
                    z-index: 5000 !important;
                }
                `}
            </style>
            <Modal
                title={
                    <Space>
                        <MedicineBoxOutlined />
                        Two-Step Administration
                        {selectedPatient && (
                            <Tag color="blue">Patient: {selectedPatient.full_name}</Tag>
                        )}
                    </Space>
                }
                open={showAdminModal}
                onCancel={() => {
                    setShowAdminModal(false);
                    setSelectedMedication(null);
                }}
                width={1200}
                footer={null}
                zIndex={5000}
                style={{ zIndex: 5000 }}
                maskStyle={{ zIndex: 4999 }}
                wrapClassName="administration-session-modal"
            >
                <AdministrationSessionManager
                    patient={selectedPatient}
                    medication={selectedMedication}
                    visible={showAdminModal}
                    onClose={() => {
                        setShowAdminModal(false);
                        setSelectedMedication(null);
                    }}
                    onComplete={() => {
                        loadMedicationDashboard(); // Refresh after completion
                        setShowAdminModal(false);
                        setSelectedMedication(null);
                        message.success('Medication administered successfully!');
                    }}
                />
            </Modal>
        </div>
    );
};

export default SmartMedicationDashboard;